import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, BookOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";

const inputCls =
  "rounded-none border-[#D4D4D4] dark:border-[#1F1F1F] dark:bg-[#0A0A0A] dark:text-white focus-visible:ring-0 focus-visible:border-[#0A0A0A] dark:focus-visible:border-[var(--neon-cyan)] h-9 font-mono text-sm";

export default function MaterialLibrary({ materials, onUse, reload, conditions }) {
  const { t } = useI18n();
  const isLight = conditions?._mode === "day";
  const headingColor = isLight ? "#0891B2" : "#FFFFFF";
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({
    name: "", lambda_value: 0.04, mu: 1,
    category: t("library.add.default.category", "Insulation"),
    brand: "", description: "",
  });

  const submit = async () => {
    if (!form.name || !form.lambda_value) {
      return toast.error(t("library.add.required", "Name and λ are required"));
    }
    try {
      await api.post("/materials", form);
      toast.success(t("library.add.toastOk", "Material added"));
      setOpen(false);
      setForm({
        name: "", lambda_value: 0.04, mu: 1,
        category: t("library.add.default.category", "Insulation"),
        brand: "", description: "",
      });
      reload();
    } catch (e) {
      toast.error(t("library.add.toastErr", "Failed to add material"));
    }
  };

  const delMat = async (id) => {
    try {
      await api.delete(`/materials/${id}`);
      toast.success(t("library.add.deleted", "Deleted"));
      reload();
    } catch { toast.error(t("library.add.deleteErr", "Delete failed")); }
  };

  const filtered = materials.filter(m =>
    m.name.toLowerCase().includes(filter.toLowerCase()) ||
    m.category.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="material-library">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2"
          style={{ color: headingColor }}>
          <BookOpen className="w-5 h-5" style={{ color: headingColor }}/>
          {t("library.title", "Material Library")}
        </h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline"
              className="rounded-md h-9 font-mono text-xs uppercase tracking-wider"
              style={{
                color: "#FFFFFF",
                borderColor: headingColor,
                backgroundColor: headingColor,
                fontWeight: 700,
              }}
              data-testid="open-add-material">
              <Plus className="w-4 h-4 mr-1" style={{ color: "#FFFFFF" }}/>
              {t("library.custom", "Custom")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle>{t("library.add.title", "Add Custom Material")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="tech-label">{t("library.add.name", "Name")}</Label>
                <Input className={inputCls} value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="new-mat-name"/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="tech-label">{t("library.add.lambda", "λ (W/mK)")}</Label>
                  <Input type="number" step="0.001" className={inputCls} value={form.lambda_value}
                    onChange={e=>setForm({...form, lambda_value: Number(e.target.value)})} data-testid="new-mat-lambda"/>
                </div>
                <div>
                  <Label className="tech-label">{t("library.add.mu", "μ")}</Label>
                  <Input type="number" step="0.5" className={inputCls} value={form.mu}
                    onChange={e=>setForm({...form, mu: Number(e.target.value)})} data-testid="new-mat-mu"/>
                </div>
                <div>
                  <Label className="tech-label">{t("library.add.category", "Category")}</Label>
                  <Input className={inputCls} value={form.category} onChange={e=>setForm({...form, category: e.target.value})} data-testid="new-mat-category"/>
                </div>
              </div>
              <div>
                <Label className="tech-label">{t("library.add.brand", "Brand")}</Label>
                <Input className={inputCls} value={form.brand} onChange={e=>setForm({...form, brand: e.target.value})} data-testid="new-mat-brand"/>
              </div>
              <div>
                <Label className="tech-label">{t("library.add.description", "Description")}</Label>
                <Input className={inputCls} value={form.description} onChange={e=>setForm({...form, description: e.target.value})} data-testid="new-mat-desc"/>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit}
                className="rounded-none bg-[#0A0A0A] hover:bg-[#333333] text-white font-mono text-xs uppercase tracking-wider"
                data-testid="submit-material">{t("common.save", "Save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Input className={inputCls + " mb-3"} placeholder={t("library.filter", "Filter (name / category)")}
        value={filter} onChange={e=>setFilter(e.target.value)} data-testid="material-filter"/>

      <div className="border border-[#D4D4D4] dark:border-[#1F1F1F] overflow-y-auto neon-scroll"
        style={{ maxHeight: "min(70vh, 620px)", minHeight: 420 }}>
        {filtered.map((m) => (
          <div key={m.id}
            className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-[#D4D4D4] dark:border-[#1F1F1F] last:border-b-0 hover:bg-[#F9F9FA] dark:hover:bg-[#0F0F0F]"
            data-testid={`material-${m.id}`}>
            <div className="col-span-5">
              <div className="text-sm font-semibold" style={{ color: headingColor }}>{m.name}</div>
              <div className="tech-label">{m.category}</div>
            </div>
            <div className="col-span-3 font-mono text-xs">λ={m.lambda_value} · μ={m.mu ?? 1}</div>
            <div className="col-span-4 flex items-center justify-end gap-2">
              {m.custom && (
                <button onClick={() => delMat(m.id)}
                  className="p-1 hover:bg-[#FFE5E3] text-[#FF3B30]" data-testid={`del-mat-${m.id}`}>
                  <Trash2 className="w-4 h-4"/>
                </button>
              )}
              <Button onClick={() => onUse(m)}
                className="rounded-md h-7 px-3 font-mono text-[10px] uppercase tracking-wider transition-all"
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid #E5E7EB",
                  color: "#FF0000",
                  fontWeight: 700,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255,0,0,0.08)";
                  e.currentTarget.style.borderColor = "#FF0000";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "#E5E7EB";
                }}
                data-testid={`use-mat-${m.id}`}>+ {t("library.use", "Use")}</Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-4 text-center text-sm text-[#52525B] font-mono">{t("library.empty", "No matching materials")}</div>
        )}
      </div>
    </div>
  );
}
